// lib
import React, { useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import ClickOutside from '@aytm/lib/rfd-ui/components/basic/click-outside'

const SidebarPopup = ({ children, id, root, help, width = '600px', trigger = 'click', direction = 'righttop' }) => {
  const [show, setShow] = useState(false)

  const popupRef = useRef(null)

  const hoverTimerRef = useRef(null)

  const scrollCallback = useCallback(() => setShow(false), [setShow])

  const detachScroll = useCallback(() => {
    window.removeEventListener('scroll', scrollCallback)
    window.removeEventListener('resize', scrollCallback)
    root && root.removeEventListener('scroll', scrollCallback)
  }, [root, scrollCallback])

  // Unmounting while the popup is open never reaches onHide, so the same detach runs here —
  // otherwise the scroll listener outlives the component and hides an unmounted popup.
  useEffect(
    () => () => {
      clearTimeout(hoverTimerRef.current)
      detachScroll()
    },
    [detachScroll]
  )

  const delaySetShow = useCallback(
    value => {
      clearTimeout(hoverTimerRef.current)

      if (value) setShow(true)
      else hoverTimerRef.current = setTimeout(() => setShow(false), 100)
    },
    [setShow]
  )

  const mouseOverCallback = useCallback(() => {
    clearTimeout(hoverTimerRef.current)

    setShow(true)
  }, [setShow])
  const mouseOutCallback = useCallback(() => delaySetShow(false), [delaySetShow])

  return (
    <Popup
      ref={popupRef}
      className="PRAYTMTheme"
      targetClassName={id != undefined ? `pendo-help-stat-${id}` : ''}
      width={width}
      direction={direction}
      popupOffset={9}
      portal
      show={Boolean(show)}
      hideOtherPopupsOnShow={true}
      header={() => <div>&nbsp;</div>}
      content={() =>
        'click' == trigger ? (
          <ClickOutside
            style={{ display: 'inline-block' }}
            onClickOutside={event => {
              if (
                popupRef.current.refPopupTarget.contains(event.target) ||
                event.target.isSameNode(popupRef.current.refPopupTarget) ||
                popupRef.current.refPopup.refPopup.contains(event.target) ||
                event.target.isSameNode(popupRef.current.refPopup.refPopup)
              )
                return
              setShow(false)
            }}
          >
            {React.isValidElement(help) ? help : <div dangerouslySetInnerHTML={{ __html: help }}></div>}
          </ClickOutside>
        ) : React.isValidElement(help) ? (
          help
        ) : (
          <div dangerouslySetInnerHTML={{ __html: help }}></div>
        )
      }
      onShow={() => {
        if ('hover' == trigger) {
          popupRef.current.refPopup.refPopup.addEventListener('mouseenter', mouseOverCallback)
          popupRef.current.refPopup.refPopup.addEventListener('mouseleave', mouseOutCallback)
        }

        window.addEventListener('scroll', scrollCallback)
        window.addEventListener('resize', scrollCallback)
        root && root.addEventListener('scroll', scrollCallback)
      }}
      onHide={detachScroll}
    >
      {React.isValidElement(children) ? (
        React.cloneElement(children, {
          onMouseEnter: 'hover' == trigger ? () => delaySetShow(true) : null,
          onMouseLeave: 'hover' == trigger ? () => delaySetShow(false) : null,
          onClick: 'click' == trigger ? () => setShow(!show) : null
        })
      ) : (
        <i
          className="icon-aytmfd-help"
          onMouseEnter={'hover' == trigger ? () => delaySetShow(true) : null}
          onMouseLeave={'hover' == trigger ? () => delaySetShow(false) : null}
          onClick={'click' == trigger ? () => setShow(!show) : null}
        />
      )}
    </Popup>
  )
}

SidebarPopup.propTypes = {
  children: PropTypes.any,
  id: PropTypes.string,
  root: PropTypes.any,
  className: PropTypes.string,
  help: PropTypes.any,
  width: PropTypes.string,
  trigger: PropTypes.string
}

SidebarPopup.displayName = 'SidebarPopup'

export default SidebarPopup
